/**
 * Grupo de escolhas em forma de etiquetas.
 *
 * **Por que botões e não caixas de seleção.** Numa tela de celular, a área
 * tocável de um `checkbox` é o quadradinho; aqui, é a etiqueta inteira. O
 * conteúdo destas telas é quase todo escolha múltipla, e errar o toque numa
 * lista de nove equipamentos é frustrante o bastante para justificar a troca.
 *
 * A semântica é preservada à mão, e é ela que faz o leitor de tela anunciar
 * corretamente: `role="group"` com `aria-pressed` para múltipla escolha,
 * `role="radiogroup"` com `aria-checked` para escolha única. Sem isso seriam
 * botões soltos, e quem navega por áudio não saberia que formam um conjunto
 * nem quantos itens ele tem.
 */

import { useId } from 'react';

export interface ChipOption<T extends string | number> {
  value: T;
  label: string;
}

/**
 * A marca de campo opcional.
 *
 * Existe porque a ausência dela foi lida como o contrário: sem nada indicando o
 * que é dispensável, quem preenche assume que tudo é exigido e desiste no meio.
 * Marcar o opcional é mais honesto do que marcar o obrigatório quando quase
 * tudo é opcional — a marca fica onde é exceção, não onde é regra.
 *
 * Em peso e cor menores que o rótulo: é informação de apoio, e competir com o
 * nome do campo atrapalharia a leitura.
 */
export function OptionalMark({ label }: { label: string }) {
  return <span className="ml-2 text-xs font-normal text-(--color-sand-500)">({label})</span>;
}

interface ChipGroupProps<T extends string | number> {
  legend: string;
  hint?: string;
  options: ReadonlyArray<ChipOption<T>>;
  selected: ReadonlyArray<T>;
  onChange: (next: T[]) => void;
  /** `false` para escolha única, em que marcar um desmarca o anterior. */
  multiple?: boolean;
  /** Marca visível de campo opcional. Ver `OptionalMark`. */
  optionalLabel?: string;
}

export function ChipGroup<T extends string | number>({
  legend,
  hint,
  options,
  selected,
  onChange,
  multiple = true,
  optionalLabel,
}: ChipGroupProps<T>) {
  const labelId = useId();

  function toggle(value: T) {
    if (!multiple) {
      onChange([value]);
      return;
    }
    onChange(
      selected.includes(value) ? selected.filter((item) => item !== value) : [...selected, value],
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <span id={labelId} className="text-sm font-semibold text-(--color-sand-900)">
        {legend}
        {optionalLabel ? <OptionalMark label={optionalLabel} /> : null}
      </span>
      {hint ? <span className="text-sm text-(--color-sand-700)">{hint}</span> : null}

      <div
        role={multiple ? 'group' : 'radiogroup'}
        aria-labelledby={labelId}
        className="flex flex-wrap gap-2"
      >
        {options.map((option) => {
          const active = selected.includes(option.value);
          return (
            <button
              key={String(option.value)}
              type="button"
              role={multiple ? undefined : 'radio'}
              aria-pressed={multiple ? active : undefined}
              aria-checked={multiple ? undefined : active}
              onClick={() => toggle(option.value)}
              className={[
                'rounded-full border-2 px-4 py-2 text-sm font-medium transition-colors',
                active
                  ? 'border-(--color-amber-500) bg-(--color-amber-100) text-(--color-sand-900)'
                  : 'border-(--color-sand-200) bg-(--color-sand-0) text-(--color-sand-700)',
              ].join(' ')}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
